import React, { useState } from 'react';
import { X, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACTS = [
  'anusha.bhupati@exoralabs.com',
  'nandini.k@exoralabs.com',
  'drrohithg@gmail.com',
];

const DemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const subject = `Demo Request from ${formData.name} - ${formData.company}`;
    const bodyParts = [
      `Hi Skydoc Team,`,
      ``,
      `I'd like to request a demo of Skydoc AI.`,
      ``,
      `--- Contact Details ---`,
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Organization: ${formData.company}`,
      formData.role ? `Role: ${formData.role}` : null,
      formData.phone ? `Phone: ${formData.phone}` : null,
      ``,
      formData.message ? `--- What I'd like to see ---\n${formData.message}` : null,
      ``,
      `Looking forward to hearing from you.`,
      ``,
      `Best regards,`,
      formData.name,
    ].filter(Boolean).join('\n');

    const mailto = `mailto:${CONTACTS.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyParts)}`;
    window.open(mailto, '_blank');

    // Also save to backend for admin dashboard tracking
    axios.post(`${API}/demo-request`, {
      name: formData.name,
      email: formData.email,
      company: formData.company,
      role: formData.role || undefined,
      phone: formData.phone || undefined,
      message: formData.message || undefined
    }).catch(() => {});

    setSubmitted(true);
    setTimeout(() => {
      onClose();
      setSubmitted(false);
      setFormData({ name: '', email: '', company: '', role: '', phone: '', message: '' });
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="demo-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
        data-testid="demo-modal-backdrop"
      />
      
      {/* Modal */}
      <div className="relative bg-white text-slate-900 force-light-text rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Book a Demo</h2>
            <p className="text-sm text-slate-500">See Skydoc AI in action</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="demo-modal-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Email Client Opened!</h3>
              <p className="text-slate-600 text-sm">Your demo request details have been pre-filled. Just hit send in your email app.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="John Smith"
                    data-testid="demo-form-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="john@hospital.com"
                    data-testid="demo-form-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Organization *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="City Hospital"
                    data-testid="demo-form-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Role
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="Chief Medical Officer"
                    data-testid="demo-form-role"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="+91 98765 43210"
                  data-testid="demo-form-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  What would you like to see?
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                  placeholder="Tell us about your clinical workflow challenges..."
                  data-testid="demo-form-message"
                />
              </div>

              <button
                type="submit"
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="demo-form-submit"
              >
                <Mail className="w-5 h-5" />
                Send Demo Request via Email
              </button>

              <p className="text-xs text-slate-500 text-center">
                This will open your email app with your details pre-filled, addressed to the Skydoc team.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
